import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, X, Calendar, UserPlus, Info, FileText, ChevronDown, ChevronUp, Image as ImageIcon, Search, Trash2, Edit2, GripVertical, Percent } from 'lucide-react';
import './AddCreditNoteForm.css';
import MediaLibrary from '../../components/MediaLibrary';
import AddProductForm from '../../components/LeadForm/AddProductForm';
import { getLeadCustomerSuggection, getInvoiceCurrencyList, getInvoiceSuggestionsCreditNote, getSettingBySection, getProductForSuggetion, getTaxesForSuggetion, insertNewCreditNote, isCreditNoteNumberAlreadyExists } from '../../utils/lead';
import { getSession } from '../../getSession';
import Swal from 'sweetalert2';

const formatDateToYYYYMMDD = (dateStr) => {
    if (!dateStr) return '';
    // Already in YYYY-MM-DD format — return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    try {
        // Replace dashes with spaces so "26-May-2026" parses correctly
        const cleanStr = dateStr.replace(/-/g, ' ');
        const d = new Date(cleanStr + ' 00:00:00'); // force local timezone
        if (isNaN(d.getTime())) {
            // Fallback: try appending T00:00:00 to avoid UTC-midnight offset
            const d2 = new Date(dateStr + 'T00:00:00');
            if (isNaN(d2.getTime())) return '';
            const y = d2.getFullYear();
            const mo = String(d2.getMonth() + 1).padStart(2, '0');
            const dy = String(d2.getDate()).padStart(2, '0');
            return `${y}-${mo}-${dy}`;
        }
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const dy = String(d.getDate()).padStart(2, '0');
        return `${y}-${mo}-${dy}`;
    } catch (e) {
        return '';
    }
};

const formatDateToDDMMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    try {
        // Append T00:00:00 to treat as local midnight, avoiding UTC-midnight
        // off-by-one in timezones like IST (+05:30)
        const safeStr = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
            ? dateStr + 'T00:00:00'
            : dateStr.replace(/-/g, ' ');
        const d = new Date(safeStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
    } catch (e) {
        return dateStr;
    }
};

const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const AddCreditNoteForm = ({ onBack, editData }) => {
    const [items, setItems] = useState([]);
    const [creditNoteNumber, setCreditNoteNumber] = useState('');
    const [creditNoteDate, setCreditNoteDate] = useState(getTodayDateString());
    const [issuedDate, setIssuedDate] = useState('');
    const [companyData, setCompanyData] = useState({});
    const [isCompanyCollapsed, setIsCompanyCollapsed] = useState(true);
    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
    const [companyLogo, setCompanyLogo] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [currencies, setCurrencies] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [showCustomerSearch, setShowCustomerSearch] = useState(false);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceSearchMessage, setInvoiceSearchMessage] = useState('');

    const [itemSearchText, setItemSearchText] = useState('');
    const [itemSuggestions, setItemSuggestions] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [showItemSearch, setShowItemSearch] = useState(false);
    const [taxSearchText, setTaxSearchText] = useState('');
    const [taxSuggestions, setTaxSuggestions] = useState([]);
    const [isLoadingTaxes, setIsLoadingTaxes] = useState(false);
    const [activeTaxSearchIndex, setActiveTaxSearchIndex] = useState(null);
    const [editId, setEditId] = useState(0);
    const [isAddProductOpen, setIsAddProductOpen] = useState(false);
    const { userId, TokenId, parentId } = getSession();

    useEffect(() => {
        if (editData) return; // Skip default init when editing — editData useEffect handles it
        const initializeSettings = async () => {
            try {
                const [companySettings, creditNoteSettings, currencyList] = await Promise.all([
                    getSettingBySection({ UserId: userId, Section: 'company' }),
                    getSettingBySection({ UserId: userId, Section: 'creditnote' }),
                    getInvoiceCurrencyList()
                ]);

                setCurrencies(currencyList);
                const getVal = (list, key) => list.find(s => s.Key?.toLowerCase() === key.toLowerCase())?.Value || '';

                // Map Company Settings
                const mappedCompany = {
                    companyName: getVal(companySettings, 'LegalName'),
                    companyEmail: getVal(companySettings, 'Email'),
                    companyPhone: getVal(companySettings, 'Phone'),
                    companyAddress: getVal(companySettings, 'HeadOffice'),
                    taxNumber: getVal(companySettings, 'TaxNumber'),
                    panNumber: getVal(companySettings, 'PanNumber'),
                    logo: getVal(companySettings, 'HttpLogo') || getVal(companySettings, 'Logo'),
                    placeOfOrigin: getVal(companySettings, 'State'),
                    companyCountry: getVal(companySettings, 'Country'),
                    companyCountryId: getVal(companySettings, 'CountryId'),
                    companyState: getVal(companySettings, 'State'),
                    companyStateId: getVal(companySettings, 'StateId'),
                };
                setCompanyData(mappedCompany);
                if (mappedCompany.logo) setCompanyLogo(mappedCompany.logo);

                // Generate Credit Note Number
                const nextNum = getVal(creditNoteSettings, 'NextNumber');
                const prefix = getVal(creditNoteSettings, 'NumberPrefix');
                const finYearFormat = getVal(creditNoteSettings, 'FinancialYearFormat');
                const numDigit = parseInt(getVal(creditNoteSettings, 'NumberDigit')) || 0;

                let finStr = '';
                const today = new Date();
                const nextYear = new Date();
                nextYear.setFullYear(today.getFullYear() + 1);

                const getYearStr = (date, format) => {
                    const yyyy = date.getFullYear();
                    const yy = String(yyyy).slice(-2);
                    return format === 'YYYY' ? yyyy : yy;
                };

                switch (finYearFormat) {
                    case 'YY-YY': finStr = `${getYearStr(today, 'YY')}-${getYearStr(nextYear, 'YY')}`; break;
                    case 'YYYY-YYYY': finStr = `${getYearStr(today, 'YYYY')}-${getYearStr(nextYear, 'YYYY')}`; break;
                    case 'YYYY-YY': finStr = `${getYearStr(today, 'YYYY')}-${getYearStr(nextYear, 'YY')}`; break;
                    case 'YYYY/YY': finStr = `${getYearStr(today, 'YYYY')}/${getYearStr(nextYear, 'YY')}`; break;
                    case 'YY/YY': finStr = `${getYearStr(today, 'YY')}/${getYearStr(nextYear, 'YY')}`; break;
                    case 'YYYY/YYYY': finStr = `${getYearStr(today, 'YYYY')}/${getYearStr(nextYear, 'YYYY')}`; break;
                }

                const paddedNum = nextNum.padStart(numDigit || nextNum.length, '0');
                const generatedCNNumber = `${prefix}${prefix && '/'}${finStr}${finStr && '/'}${paddedNum}`;
                setCreditNoteNumber(generatedCNNumber);

                const dueDays = parseInt(getVal(creditNoteSettings, 'PaymentDueDays')) || 30;
                const dueDate = new Date();
                dueDate.setDate(today.getDate() + dueDays);
                setIssuedDate(dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-'));

                // Set default currency to INR
                const inrCurrency = currencyList.find(c => c.Name?.toLowerCase().includes('rupee') || c.Code === 'INR');
                if (inrCurrency) {
                    setSelectedCurrency(inrCurrency.Code || 'INR');
                } else if (currencyList.length > 0) {
                    setSelectedCurrency(currencyList[0].Code || 'INR');
                }
            } catch (err) {
                console.error('Error initializing settings:', err);
            }
        };
        initializeSettings();
    }, [userId]);

    // Pre-populate form when editing
    useEffect(() => {
        if (!editData) return;
        setEditId(editData.id || 0);
        setCreditNoteNumber(editData.credit_note_number || '');
        setCreditNoteDate(formatDateToYYYYMMDD(editData.CreatedDate || editData.created_at) || getTodayDateString());
        setIssuedDate(editData.DueAt || editData.issued_at || '');
        if (editData.company_logo) setCompanyLogo(editData.company_logo);
        setCompanyData({
            companyName: editData.company_name || '',
            companyEmail: editData.company_email || '',
            companyPhone: editData.company_phone || '',
            companyAddress: editData.company_address || '',
            taxNumber: editData.company_tax_number || '',
            panNumber: editData.CompanyPanNumber || '',
            logo: editData.company_logo || '',
        });
        // Synthetic customer object
        setSelectedCustomer({
            LeadId: editData.leadid || 0,
            PersonName: editData.contact_name || '',
            EmailID: editData.contact_email || '',
            MobileNo: editData.contact_phone || '',
            Address: editData.contact_address || '',
            TaxNumber: editData.contact_tax_number || '',
            PanNumber: editData.ContactPanNumber || '',
            CountryId: editData.BillToCountryId || 0,
            StateId: editData.BillToStateId || 0,
            CityId: editData.BillToCityId || 0,
            PinCode: editData.BillToPincode || '',
        });
        setCustomerSearch(editData.contact_name || '');
        // Map items
        if (Array.isArray(editData.creditNoteItems) && editData.creditNoteItems.length > 0) {
            const mapped = editData.creditNoteItems.map((item, i) => {
                const taxes = item.creditnoteItemTaxes || [];
                const discTax = taxes.find(t => t.Name?.toLowerCase() === 'discount');
                const regTax = taxes.find(t => t.Name?.toLowerCase() !== 'discount');
                return {
                    id: `item_edit_${i}_${Date.now()}`,
                    name: item.Name || '',
                    description: item.Description || '',
                    quantity: item.Quantity || 1,
                    price: item.Price || 0,
                    amount: item.Total || 0,
                    itemId: item.Id || 0,
                    isTaxEnabled: !!regTax,
                    isDiscountEnabled: !!discTax,
                    taxPercent: regTax?.Rate || 0,
                    taxAmount: regTax?.Amount || 0,
                    taxName: regTax?.Name || 'GST',
                    taxId: regTax?.TaxId || 0,
                    discountPercent: discTax?.Type?.trim().toLowerCase() === 'p' ? (discTax.Rate || 0) : 0,
                    discountAmount: discTax?.Amount || 0,
                    isDiscountPercentage: true,
                };
            });
            setItems(mapped);
        }
    }, [editData]);

    // Set currency once currencies list is loaded in edit mode
    useEffect(() => {
        if (!editData || !editData.CurrencyId || currencies.length === 0) return;
        const match = currencies.find(c => String(c.Id) === String(editData.CurrencyId));
        if (match) setSelectedCurrency(match.Code || 'INR');
    }, [editData, currencies]);

    useEffect(() => {
        if (customerSearch.length > 2) {
            const delayDebounceFn = setTimeout(async () => {
                const payload = {
                    Details: {
                        UserId: userId,
                        FilterText: customerSearch
                    }
                };
                const data = await getLeadCustomerSuggection(payload);
                setCustomerSuggestions(data);
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        } else {
            setCustomerSuggestions([]);
        }
    }, [customerSearch, userId]);

    useEffect(() => {
        if (invoiceSearch.length > 1) {
            if (!selectedCustomer) {
                setInvoiceSearchMessage('Please select a customer first to search for invoices');
                setInvoiceSuggestions([]);
                return;
            }

            const delayDebounceFn = setTimeout(async () => {
                const params = {
                    LeadId: selectedCustomer.LeadId || selectedCustomer.ID || 0,
                    FilterText: invoiceSearch
                };
                const data = await getInvoiceSuggestionsCreditNote(params);
                setInvoiceSuggestions(data);
                if (data.length === 0) {
                    setInvoiceSearchMessage('No result matching unpaid invoices for this customer');
                } else {
                    setInvoiceSearchMessage('');
                }
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        } else {
            setInvoiceSuggestions([]);
            setInvoiceSearchMessage('');
        }
    }, [invoiceSearch, selectedCustomer]);

    const handleSelectMedia = (media) => {
        setCompanyLogo(media.url);
        setIsMediaLibraryOpen(false);
    };

    const fetchItemSuggestions = async (val) => {
        setItemSearchText(val);
        if (val.length > 2) {
            setIsLoadingItems(true);
            try {
                const list = await getProductForSuggetion({ FilterText: val, UserId: userId, SectionName: 'invoice' });
                setItemSuggestions(Array.isArray(list) ? list : []);
            } catch (err) {
                console.warn('fetchItemSuggestions error', err);
            } finally {
                setIsLoadingItems(false);
            }
        } else {
            setItemSuggestions([]);
        }
    };

    const handleSelectItem = (prod) => {
        let hsnCode = '';
        let isTaxable = false;
        let productDetails = {};
        try {
            if (prod.ProductJSON) {
                productDetails = JSON.parse(prod.ProductJSON);
                hsnCode = productDetails.HSNCode || productDetails.SACCode || '';
                isTaxable = productDetails.IsTaxable === true || productDetails.IsTaxable === 1;
            }
        } catch (e) {
            console.warn('Parse ProductJSON error', e);
        }

        const newItem = {
            id: `item_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
            name: prod.Name || '',
            description: '',
            quantity: 1,
            price: prod.SalePrice || 0,
            amount: prod.SalePrice || 0,
            hsnCode: hsnCode,
            taxPercent: isTaxable ? 18 : 0, // Default 18% if taxable
            taxAmount: 0,
            discountPercent: 0,
            discountAmount: 0,
            isTaxEnabled: false,
            isDiscountEnabled: false,
            isDiscountPercentage: true,
            taxName: 'GST',
            itemId: prod.Id || 0,
            itemSKU: prod.Sku || ''
        };

        // Initial calculation
        const baseAmount = newItem.quantity * newItem.price;
        newItem.amount = baseAmount;

        setItems([...items, newItem]);
        setShowItemSearch(false);
        setItemSearchText('');
        setItemSuggestions([]);
    };

    const updateItem = (index, fieldOrObject, value) => {
        const newItems = [...items];
        let item = { ...newItems[index] };

        if (typeof fieldOrObject === 'object') {
            Object.assign(item, fieldOrObject);
        } else {
            item[fieldOrObject] = value;
        }

        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        const baseAmount = qty * price;
        item.amount = baseAmount;

        // Recalculate Discount
        let dAmount = 0;
        if (item.isDiscountEnabled) {
            if (item.isDiscountPercentage) {
                const dPercent = parseFloat(item.discountPercent) || 0;
                dAmount = (baseAmount * dPercent) / 100;
                item.discountAmount = dAmount;
            } else {
                dAmount = parseFloat(item.discountAmount) || 0;
            }
        } else {
            item.discountAmount = 0;
            item.discountPercent = 0;
        }

        // Recalculate Tax
        if (item.isTaxEnabled) {
            const tPercent = parseFloat(item.taxPercent) || 0;
            const taxableAmount = baseAmount - dAmount;
            item.taxAmount = (taxableAmount * tPercent) / 100;
        } else {
            item.taxAmount = 0;
            item.taxPercent = 0;
        }

        newItems[index] = item;
        setItems(newItems);
    };

    const handleTaxSearch = async (index, text) => {
        setTaxSearchText(text);
        if (text.length < 1) {
            setTaxSuggestions([]);
            return;
        }
        setIsLoadingTaxes(true);
        try {
            const results = await getTaxesForSuggetion({ FilterText: text, UserId: userId });
            setTaxSuggestions(results || []);
        } catch (e) {
            console.error('Tax search error', e);
        } finally {
            setIsLoadingTaxes(false);
        }
    };

    const handleSelectTax = (itemIndex, tax) => {
        updateItem(itemIndex, {
            taxPercent: tax.Rate,
            taxName: tax.Name,
            taxId: tax.Id
        });
        setActiveTaxSearchIndex(null);
        setTaxSearchText('');
        setTaxSuggestions([]);
    };

    const handleSave = async () => {
        // Validation
        if (!selectedCustomer) {
            Swal.fire('Validation Failed', 'Customer detail is required.', 'warning');
            return;
        }

        if (!creditNoteNumber) {
            Swal.fire('Validation Failed', 'Credit Note Number is required.', 'warning');
            return;
        }

        if (items.length === 0) {
            Swal.fire('Validation Failed', 'At least one product required for invoice', 'warning');
            return;
        }

        // Check if Credit Note Number exists
        // try {
        //     const exists = await isCreditNoteNumberAlreadyExists({
        //         CreditNumber: creditNoteNumber,
        //         UserId: userId
        //     });
        //     if (exists && typeof exists === 'string') {
        //         Swal.fire('Validation Failed', exists, 'warning');
        //         return;
        //     }
        // } catch (e) {
        //     console.error('Check existence error', e);
        // }

        const subTotal = items.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
        const totalDiscount = items.reduce((acc, item) => acc + (parseFloat(item.discountAmount) || 0), 0);
        const totalTax = items.reduce((acc, item) => acc + (parseFloat(item.taxAmount) || 0), 0);
        const grandTotal = subTotal - totalDiscount + totalTax;

        const creditData = {
            Id: editId,
            BillToLeadId: selectedCustomer.LeadId || selectedCustomer.ID || 0,
            BillToName: selectedCustomer.PersonName || '',
            BillToTaxNumber: selectedCustomer.TaxNumber || '',
            BillToEmailId: selectedCustomer.EmailID || '',
            BillToPhoneNumber: selectedCustomer.MobileNo || '',
            BillToAddress: selectedCustomer.Address || '',
            CompanyName: companyData.companyName || '',
            CompanyEmail: companyData.companyEmail || '',
            CompanyPhone: companyData.companyPhone || '',
            CompanyAddress: companyData.companyAddress || '',
            CompanyTaxNumber: companyData.taxNumber || '',
            CompanyPanNumber: companyData.panNumber || '',
            CurrencyId: currencies.find(c => c.Code === selectedCurrency)?.Id || 113,
            CompanyLogo: companyLogo || '',
            CreditTitle: 'Credit Note',
            CreditSubHeading: '',
            InvoiceNumber: selectedInvoice?.InvoiceNumber || '',
            creditNoteNumber: creditNoteNumber,
            BillToPanNumber: selectedCustomer.PanNumber || '',
            BillToCountryId: selectedCustomer.CountryId || 0,
            BillToStateId: selectedCustomer.StateId || 0,
            BillToCityId: selectedCustomer.CityId || 0,
            BillToPincode: selectedCustomer.PinCode || '',
            SalesOwner: 0,
            CreditDate: creditNoteDate,
            IssueDate: issuedDate,
            CreditSubTotal: subTotal,
            CreditTotal: grandTotal,
            Items: items.map(item => ({
                itemInvoiceId: selectedInvoice?.InvoiceId || 0,
                ItemName: item.name,
                ItemId: item.itemId || 0,
                ItemSKU: item.itemSKU || '',
                ItemDescription: item.description,
                AdditionalEntities: {},
                ItemVolume: parseFloat(item.quantity) || 0,
                ItemPrice: parseFloat(item.price) || 0,
                ItemAmount: parseFloat(item.amount) || 0,
                ItemTaxCharges: item.isTaxEnabled ? [{
                    TCName: item.taxName || 'GST',
                    TCId: item.taxId || 0,
                    TCRate: parseFloat(item.taxPercent) || 0,
                    TCType: 'P',
                    TCAmount: parseFloat(item.taxAmount) || 0
                }] : []
            }))
        };

        try {
            const response = await insertNewCreditNote({
                CreditData: JSON.stringify(creditData),
                UserId: userId
            });

            if (response) {
                Swal.fire('Success', 'Credit Note data saved successfully', 'success');
                onBack();
            } else {
                Swal.fire('OOps', 'Unable to save Credit Note detail please contact admin or support team.', 'warning');
            }
        } catch (err) {
            Swal.fire('Error', 'An error occurred while saving.', 'error');
        }
    };

    return (
        <div className="add-credit-note-form">
            <div className="form-header">
                <button className="back-btn" onClick={onBack}>
                    <X size={18} />
                    <span>Back to List</span>
                </button>
            </div>

            <div className="form-title-bar">
                {editId > 0 ? 'Edit Credit Note' : 'New Credit Note'}
            </div>

            <div className="form-content">
                <div className="section-header company-header" onClick={() => setIsCompanyCollapsed(!isCompanyCollapsed)}>
                    <div className="header-left">
                        <div className="section-icon">L</div>
                        <span>Company</span>
                    </div>
                    <div className="header-right">
                        {isCompanyCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                    </div>
                </div>

                {!isCompanyCollapsed && (
                    <div className="section-body company-body">
                        <div className="company-details-info">
                            <div className="media-upload-area" onClick={() => setIsMediaLibraryOpen(true)}>
                                {companyLogo ? (
                                    <img src={companyLogo} alt="Logo" className="uploaded-logo-main" />
                                ) : (
                                    <div className="upload-placeholder">
                                        <div className="upload-text">DROP FILES HERE TO UPLOAD</div>
                                    </div>
                                )}
                            </div>
                            <div className="company-text-details">
                                <h3 className="company-name-display">{companyData.companyName || 'Company Name'}</h3>
                                <p className="company-sub-detail">{companyData.companyEmail}</p>
                                <p className="company-sub-detail">{companyData.companyPhone}</p>
                                <p className="company-sub-detail">{companyData.companyAddress}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="visible-form-section">
                    <div className="form-grid">
                        <div className="customer-selection-box-new">
                            {!showCustomerSearch && !selectedCustomer ? (
                                <div className="add-customer-trigger-box" onClick={() => setShowCustomerSearch(true)}>
                                    <UserPlus size={20} />
                                    <span>Add a Customer</span>
                                </div>
                            ) : (
                                <>
                                    <div className="search-field-container">
                                        <label>Type a Customer name</label>
                                        <div className="search-input-box">
                                            <input
                                                type="text"
                                                placeholder="Search customer..."
                                                value={customerSearch}
                                                onChange={(e) => setCustomerSearch(e.target.value)}
                                                autoFocus
                                            />
                                            <Search size={16} />
                                            <button className="close-search-btn" onClick={() => {
                                                setShowCustomerSearch(false);
                                                if (!selectedCustomer) setCustomerSearch('');
                                            }}>
                                                <X size={14} />
                                            </button>
                                        </div>
                                        {customerSuggestions.length > 0 && (
                                            <div className="suggestions-dropdown">
                                                {customerSuggestions.map((cust) => (
                                                    <div
                                                        key={cust.LeadId}
                                                        className="suggestion-item"
                                                        onClick={() => {
                                                            setSelectedCustomer(cust);
                                                            setCustomerSearch(cust.PersonName);
                                                            setCustomerSuggestions([]);
                                                            setShowCustomerSearch(false);
                                                        }}
                                                    >
                                                        {cust.PersonName}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {selectedCustomer && (
                                        <div className="selected-customer-info" onClick={() => setShowCustomerSearch(true)}>
                                            <p><strong>{selectedCustomer.PersonName}</strong></p>
                                            <p>{selectedCustomer.EmailID}</p>
                                            <p>{selectedCustomer.MobileNo}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="main-fields">
                            <div className="field-group">
                                <label>Credit Note Number</label>
                                <div className="input-with-icon">
                                    <FileText size={16} />
                                    <input type="text" value={creditNoteNumber} readOnly />
                                </div>
                            </div>

                            <div className="field-group">
                                <label>Credit Note Date</label>
                                <div className="input-with-icon">
                                    <Calendar size={16} />
                                    <input
                                        type="date"
                                        value={formatDateToYYYYMMDD(creditNoteDate)}
                                        min={getTodayDateString()}
                                        onChange={(e) => setCreditNoteDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="field-group">
                                <label>Issued Date</label>
                                <div className="input-with-icon">
                                    <Calendar size={16} />
                                    <input
                                        type="date"
                                        value={formatDateToYYYYMMDD(issuedDate)}
                                        onChange={(e) => setIssuedDate(formatDateToDDMMMYYYY(e.target.value))}
                                    />
                                </div>
                            </div>

                            <div className="field-group full-width relative">
                                <label>Invoice Reference</label>
                                <div className="invoice-search-container">
                                    <input
                                        type="text"
                                        placeholder="Type an item name"
                                        value={invoiceSearch}
                                        onChange={(e) => setInvoiceSearch(e.target.value)}
                                    />
                                    {invoiceSuggestions.length > 0 && (
                                        <div className="invoice-suggestions-dropdown">
                                            {invoiceSuggestions.map((inv) => (
                                                <div
                                                    key={inv.InvoiceId}
                                                    className="invoice-suggestion-item"
                                                    onClick={() => {
                                                        setSelectedInvoice(inv);
                                                        setInvoiceSearch(inv.InvoiceNumber);
                                                        setInvoiceSuggestions([]);
                                                        setInvoiceSearchMessage('');
                                                    }}
                                                >
                                                    <span className="inv-num">{inv.InvoiceNumber}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {invoiceSearchMessage && (
                                        <div className="invoice-search-message">
                                            {invoiceSearchMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="currency-section">
                    <label>Currency</label>
                    <div className="currency-dropdown">
                        <div className="icon-box">
                            <ImageIcon size={14} />
                        </div>
                        <select value={selectedCurrency} onChange={(e) => setSelectedCurrency(e.target.value)}>
                            {currencies.map((curr) => (
                                <option key={curr.Id} value={curr.Code}>
                                    {curr.Symbol} {curr.Name} ({curr.Code})
                                </option>
                            ))}
                            {currencies.length === 0 && <option value="INR">₹ INR</option>}
                        </select>
                    </div>
                </div>
            </div>

            <div className="items-table-container">
                <table className="items-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>ITEMS</th>
                            <th>DESCRIPTION</th>
                            <th>QUANTITY</th>
                            <th>PRICE</th>
                            <th>AMOUNT</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <React.Fragment key={item.id}>
                                <tr className="item-row">
                                    <td className="drag-handle-cell"><GripVertical size={16} className="drag-icon" /></td>
                                    <td>
                                        <div className="input-with-edit">
                                            <input type="text" placeholder="Item name" value={item.name} onChange={(e) => updateItem(index, 'name', e.target.value)} />
                                            <Edit2 size={12} className="edit-icon" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="input-with-edit">
                                            <input type="text" placeholder="Description" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} />
                                            <Edit2 size={12} className="edit-icon" />
                                        </div>
                                    </td>
                                    <td><input type="number" className="qty-input" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} /></td>
                                    <td>
                                        <div className="price-input-wrapper">
                                            <span>₹</span>
                                            <input type="number" value={item.price} onChange={(e) => updateItem(index, 'price', e.target.value)} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="amount-display">
                                            <span>₹</span>
                                            <span>{(item.amount || 0).toFixed(2)}</span>
                                        </div>
                                    </td>
                                    <td className="action-cell">
                                        <button onClick={() => setItems(items.filter((_, i) => i !== index))} className="remove-item-btn">
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                                {(item.isDiscountEnabled || item.isTaxEnabled) && (
                                    <tr className="sub-item-inputs">
                                        <td colSpan={7}>
                                            <div className="sub-inputs-container">
                                                {item.isDiscountEnabled && (
                                                    <div className="sub-input-row discount-row">
                                                        <div className="sub-label">Discount</div>
                                                        <div className="sub-controls">
                                                            <div className="toggle-group">
                                                                <button
                                                                    className={item.isDiscountPercentage ? 'active' : ''}
                                                                    onClick={() => updateItem(index, 'isDiscountPercentage', true)}
                                                                >%</button>
                                                                <button
                                                                    className={!item.isDiscountPercentage ? 'active' : ''}
                                                                    onClick={() => updateItem(index, 'isDiscountPercentage', false)}
                                                                >₹</button>
                                                            </div>
                                                            <input
                                                                type="number"
                                                                value={item.isDiscountPercentage ? item.discountPercent : item.discountAmount}
                                                                onChange={(e) => updateItem(index, item.isDiscountPercentage ? 'discountPercent' : 'discountAmount', e.target.value)}
                                                            />
                                                            {item.isDiscountPercentage && <span className="unit-label">%</span>}
                                                        </div>
                                                        <div className="sub-amount">₹ {(item.discountAmount || 0).toFixed(2)}</div>
                                                        <button className="sub-remove" onClick={() => updateItem(index, 'isDiscountEnabled', false)}><Trash2 size={14} /></button>
                                                    </div>
                                                )}
                                                {item.isTaxEnabled && (
                                                    <div className="sub-input-row tax-row">
                                                        <div className="sub-label" onClick={() => setActiveTaxSearchIndex(index)} style={{ cursor: 'pointer', color: '#2563eb' }}>
                                                            {item.taxName || 'Select Tax'}
                                                        </div>
                                                        <div className="sub-controls tax-search-wrapper">
                                                            {activeTaxSearchIndex === index ? (
                                                                <div className="tax-search-box">
                                                                    <div className="tax-search-input">
                                                                        <input
                                                                            type="text"
                                                                            autoFocus
                                                                            placeholder="Search Tax..."
                                                                            value={taxSearchText}
                                                                            onChange={(e) => handleTaxSearch(index, e.target.value)}
                                                                            onBlur={() => {
                                                                                if (!taxSuggestions.length) {
                                                                                    setTimeout(() => setActiveTaxSearchIndex(null), 200);
                                                                                }
                                                                            }}
                                                                        />
                                                                        {isLoadingTaxes && <div className="tax-loader"></div>}
                                                                    </div>
                                                                    {taxSuggestions.length > 0 && (
                                                                        <div className="tax-suggestions">
                                                                            {taxSuggestions.map((tax) => (
                                                                                <div
                                                                                    key={tax.Id}
                                                                                    className="tax-suggestion-item"
                                                                                    onMouseDown={() => handleSelectTax(index, tax)}
                                                                                >
                                                                                    <div className="tax-name">{tax.Name}</div>
                                                                                    <div className="tax-rate">{tax.Rate}%</div>
                                                                                </div>
                                                                            ))}
                                                                            <div className="tax-suggestion-item add-new">
                                                                                <Plus size={12} /> Add New
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <input
                                                                        type="number"
                                                                        value={item.taxPercent}
                                                                        onChange={(e) => updateItem(index, 'taxPercent', e.target.value)}
                                                                    />
                                                                    <span className="unit-label">%</span>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="sub-amount">₹ {(item.taxAmount || 0).toFixed(2)}</div>
                                                        <button className="sub-remove" onClick={() => updateItem(index, 'isTaxEnabled', false)}><Trash2 size={14} /></button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                <tr className="item-actions-row">
                                    <td colSpan={7}>
                                        <div className="item-extra-actions">
                                            {!item.isDiscountEnabled && (
                                                <button onClick={() => updateItem(index, 'isDiscountEnabled', true)}>
                                                    <Plus size={12} /> Add Discount
                                                </button>
                                            )}
                                            {!item.isTaxEnabled && (
                                                <button onClick={() => {
                                                    updateItem(index, 'isTaxEnabled', true);
                                                    setActiveTaxSearchIndex(index);
                                                }}>
                                                    <Plus size={12} /> Add Tax
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>

                <div className="add-item-section">
                    {!showItemSearch ? (
                        <button className="add-item-link" onClick={() => setShowItemSearch(true)}>
                            <Plus size={14} />
                            Add an Item
                        </button>
                    ) : (
                        <div className="item-search-container" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <div className="search-input-wrapper" style={{ flex: 1 }}>
                                <Search size={16} className="search-icon" />
                                <input
                                    type="text"
                                    autoFocus
                                    placeholder="Type an item name"
                                    value={itemSearchText}
                                    onChange={(e) => fetchItemSuggestions(e.target.value)}
                                />
                                <button className="close-search" onClick={() => { setShowItemSearch(false); setItemSearchText(''); setItemSuggestions([]); }}>
                                    <Plus size={16} style={{ transform: 'rotate(45deg)' }} />
                                </button>
                            </div>
                            <button
                                onClick={() => setIsAddProductOpen(true)}
                                className="add-product-btn-inline"
                                style={{
                                    padding: '10px 16px',
                                    background: '#fff',
                                    border: '1px solid #3b82f6',
                                    color: '#3b82f6',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <Plus size={14} />
                                Add New Product
                            </button>

                            {(itemSuggestions.length > 0 || (itemSearchText.length > 0 && !isLoadingItems)) && (
                                <div className="item-suggestions-dropdown">
                                    {itemSuggestions.length > 0 ? (
                                        itemSuggestions.map((prod) => (
                                            <div
                                                key={prod.Id}
                                                onClick={() => handleSelectItem(prod)}
                                                className="suggestion-item"
                                            >
                                                <div className="prod-info">
                                                    <div className="prod-name">{prod.Name}</div>
                                                    <div className="prod-meta">
                                                        <span>Price: ₹{prod.SalePrice}</span>
                                                        {prod.Sku && <span>SKU: {prod.Sku}</span>}
                                                        {prod.IsVariant === 1 && <span className="variant-badge">Has Variants</span>}
                                                    </div>
                                                </div>
                                                <Plus size={14} />
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-suggestions-item" style={{ padding: '16px', color: '#6b7280', fontSize: '13px', textAlign: 'center' }}>
                                            No products found matching "{itemSearchText}"
                                        </div>
                                    )}
                                    <div
                                        onClick={() => setIsAddProductOpen(true)}
                                        className="add-new-prod-suggestion-btn"
                                        style={{
                                            padding: '12px 16px',
                                            background: '#eff6ff',
                                            borderTop: '1px solid #dbeafe',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            transition: 'background 0.2s'
                                        }}
                                    >
                                        <div style={{
                                            padding: '6px',
                                            background: '#3b82f6',
                                            borderRadius: '50%',
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Plus size={14} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1d4ed8' }}>Add "{itemSearchText}" as a New Product</div>
                                            <div style={{ fontSize: '10px', color: '#3b82f6', marginTop: '2px' }}>Create product profile to use it in future transactions.</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {isLoadingItems && <div className="search-loader"></div>}
                        </div>
                    )}
                </div>
            </div>

            <div className="totals-section">
                <div className="total-row">
                    <span>SUB TOTAL</span>
                    <span>₹ {items.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0).toFixed(2)}</span>
                </div>
                {items.some(item => item.isDiscountEnabled && item.discountAmount > 0) && (
                    <div className="total-row discount-total">
                        <span>DISCOUNT</span>
                        <span>₹ {items.reduce((acc, item) => acc + (parseFloat(item.discountAmount) || 0), 0).toFixed(2)}</span>
                    </div>
                )}
                {items.some(item => item.isTaxEnabled && item.taxAmount > 0) && (
                    <div className="total-row tax-total">
                        <span>GST</span>
                        <span>₹ {items.reduce((acc, item) => acc + (parseFloat(item.taxAmount) || 0), 0).toFixed(2)}</span>
                    </div>
                )}
                <div className="total-row grand-total">
                    <span>GRAND TOTAL</span>
                    <span>₹ {(
                        items.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) -
                        items.reduce((acc, item) => acc + (parseFloat(item.discountAmount) || 0), 0) +
                        items.reduce((acc, item) => acc + (parseFloat(item.taxAmount) || 0), 0)
                    ).toFixed(2)}</span>
                </div>
            </div>

            <div className="form-footer">
                <button className="cancel-btn" onClick={onBack}>Cancel</button>
                <button className="save-btn" onClick={handleSave}>Save</button>
            </div>

            <MediaLibrary
                show={isMediaLibraryOpen}
                onclose={() => setIsMediaLibraryOpen(false)}
                onSelectMedia={handleSelectMedia}
                filterOption="Images"
            />

            {isAddProductOpen && (
                <AddProductForm
                    source="Credit Note"
                    initialName={itemSearchText}
                    onClose={() => setIsAddProductOpen(false)}
                    onSuccess={(newProd) => {
                        handleSelectItem({
                            ...newProd,
                            Id: newProd.ProductID || newProd.Id,
                            Name: newProd.ProductName || newProd.Name,
                            SalePrice: newProd.SalesPrice || newProd.Price,
                            ProductJSON: JSON.stringify({
                                HSNCode: newProd.HSNCode,
                                SACCode: newProd.SACCode,
                                IsTaxable: newProd.IsTaxable === '1' || newProd.IsTaxable === 1
                            })
                        });
                    }}
                />
            )}
        </div>
    );
};

export default AddCreditNoteForm;
